import useMinimumLoading from "../../hooks/useMinimumLoading";
import GroupSkeleton from "../../components/loading/GroupSkeleton";
// src/pages/grouptrip/SessionJoinPage.tsx
import React, { useEffect, useState } from "react";
import { motion } from "motion/react";
import { useNavigate, useParams } from "react-router-dom";
import { GroupSessionService } from "../../services/GroupSessionService";

const SessionJoinPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const [status, setStatus] = useState<"joining" | "success" | "error">("joining");
  const [error, setError] = useState<string | null>(null);

  const showJoining = useMinimumLoading(status === "joining");
  useEffect(() => {
    if (status === "success" && !showJoining) navigate(`/sessions/${sessionId}`, {replace: true});
  }, [status, showJoining, sessionId, navigate]);

  // Join on mount
  useEffect(() => {
    const run = async () => {
      if (!sessionId) {
        setError("Missing session id.");
        setStatus("error");
        return;
      }
      try {
        await GroupSessionService.join(sessionId);
        setStatus("success");
      } catch (e: any) {
        setError(e?.message || "Failed to join session.");
        setStatus("error");
      }
    };
    run();
  }, [sessionId, navigate]);

  return (
    <motion.div
      className="relative min-h-screen w-full bg-gradient-to-b from-[#f6f8ff] to-[#dde5ff] flex items-center justify-center px-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
    >
      <div className="w-full max-w-md rounded-3xl bg-white/70 backdrop-blur border border-white p-8 shadow">
        {status !== "error" && (
          <div className="flex flex-col items-center text-center gap-4">
            <div className="w-full"><GroupSkeleton variant="participants" label="Du wirst zur Reise hinzugefügt"/></div>
            <h1 className="fw-unblur text-xl font-semibold text-[#232323]">Joining session…</h1>
            <p className="text-sm text-[#7a7f97]">
              We’re adding you to the group. This should only take a moment.
            </p>
          </div>
        )}

        {status === "error" && (
          <div className="flex flex-col items-center text-center gap-4">
            <h1 className="text-xl font-semibold text-rose-600">Couldn’t join</h1>
            <p className="text-sm text-[#7a7f97]">{error}</p>
            <div className="flex gap-3 mt-2">
              <button
                className="rounded-full px-5 py-2 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 transition"
                onClick={() => navigate("/sessions")}
              >
                Back to Sessions
              </button>
              <button
                className="rounded-full px-5 py-2 bg-[#A87AF0] text-white hover:brightness-110 transition"
                onClick={() => {
                  setStatus("joining");
                  setError(null);
                  // re-run by pushing same route to retrigger effect
                  // or simply call the service again here:
                  GroupSessionService.join(sessionId!)
                    .then(() => setStatus("success"))
                    .catch((e) => {
                      setError(e?.message || "Failed to join session.");
                      setStatus("error");
                    });
                }}
              >
                Try again
              </button>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default SessionJoinPage;
